import { OwnOption } from '@cabonline/ui-web';
import { usePassengerListQuery } from '@multiplatform-apps/service-trips';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { MultiValue } from 'react-select';

import { getPassengerSearchOverrides } from '../hooks/use-action-permissions';
import { MultiSelectOnChange } from './types';

interface PassengersContextType {
  selectedStatus: MultiValue<OwnOption>;
  setSelectedStatus: (newValue: MultiValue<OwnOption>) => void;
  handleStatusChange: MultiSelectOnChange;
  selectedAgreement: MultiValue<OwnOption>;
  setSelectedAgreement: (newValue: MultiValue<OwnOption>) => void;
  handleAgreementChange: MultiSelectOnChange;
  handleClearFilter: () => void;
  hasCreatePassengerPermission: boolean;
}

export const PassengersContext = React.createContext<PassengersContextType>(
  {} as PassengersContextType
);

export const PassengersProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  // Fetch page 1 to get per-endpoint permissions from the V2 response.
  // Uses itemsPerPage=10 to share the cache entry with the passenger list's initial load.
  const { data: searchData } = usePassengerListQuery({
    searchString: '',
    pageNumber: 1,
    itemsPerPage: 10,
  });
  const searchPermissions = searchData?.permissions;

  const hasCreatePassengerPermission = getPassengerSearchOverrides()?.canSeeRegisterPassengerButton ?? searchPermissions?.canSeeRegisterPassengerButton ?? false;

  const [selectedStatus, setSelectedStatus] = useState<MultiValue<OwnOption>>([]);
  const [selectedAgreement, setSelectedAgreement] = useState<MultiValue<OwnOption>>([]);

  useEffect(() => {
    const savedFilters = localStorage.getItem('service-trips:passengerFilters');
    if (savedFilters) {
      try {
        const { selectedStatus, selectedAgreement } = JSON.parse(savedFilters) as {
          selectedStatus?: MultiValue<OwnOption>;
          selectedAgreement?: MultiValue<OwnOption>;
        };
        if (selectedStatus) {
          setSelectedStatus(selectedStatus);
        }
        if (selectedAgreement) {
          setSelectedAgreement(selectedAgreement);
        }
      } catch {
        localStorage.removeItem('service-trips:passengerFilters');
      }
    }
  }, []);

  useEffect(() => {
    const filterState = {
      selectedStatus,
      selectedAgreement,
    };
    localStorage.setItem('service-trips:passengerFilters', JSON.stringify(filterState));
  }, [selectedStatus, selectedAgreement]);

  const handleAgreementChange = useCallback((newValue: MultiValue<OwnOption>) => {
    setSelectedAgreement(newValue);
  }, []);

  const handleStatusChange = useCallback((newValue: MultiValue<OwnOption>) => {
    setSelectedStatus(newValue);
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedStatus([]);
    setSelectedAgreement([]);
    localStorage.removeItem('service-trips:passengerFilters');
  }, []);

  const value = useMemo(
    () => ({
      selectedStatus,
      setSelectedStatus,
      handleStatusChange,
      selectedAgreement,
      setSelectedAgreement,
      handleAgreementChange,
      handleClearFilter,
      hasCreatePassengerPermission,
    }),
    [selectedStatus, handleStatusChange, selectedAgreement, handleAgreementChange, handleClearFilter, hasCreatePassengerPermission]
  );

  return (
    <PassengersContext.Provider value={value}>
      {children}
    </PassengersContext.Provider>
  );
};

export const usePassengers = () => React.useContext(PassengersContext);
